import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHeart,
  FaRegHeart,
  FaSmile,
  FaEllipsisV,
  FaReply,
  FaFlag,
  FaTrash,
  FaThumbtack,
  FaCheck,
  FaCheckDouble,
  FaUser,
  FaCrown,
  FaShieldAlt,
} from "react-icons/fa";
import { formatDistanceToNow } from "date-fns";
import { useChat } from "../../context/ChatContext";
import axios from "../../utils/axiosConfig";
import toast from "react-hot-toast";

const ChatMessage = ({ message, onReply, isCurrentUser }) => {
  const { user } = useSelector((state) => state.auth);
  const { addReaction, deleteMessage, pinMessage, reportMessage } = useChat();
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);

  React.useEffect(() => {
    checkDarkMode();
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const reactions = ["👍", "❤️", "😂", "😮", "🎉", "🔥"];

  const handleReaction = (emoji) => {
    addReaction(message._id, emoji);
    setShowReactions(false);
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      deleteMessage(message._id);
    }
    setShowActions(false);
  };

  const handlePin = () => {
    pinMessage(message._id);
    setShowActions(false);
  };

  const handleReport = async () => {
    if (!reportReason) {
      toast.error("Please select a reason");
      return;
    }

    const success = await reportMessage(
      message._id,
      reportReason,
      reportDescription,
    );
    if (success) {
      setShowReportModal(false);
      setReportReason("");
      setReportDescription("");
    }
    setShowActions(false);
  };

  const isAdminOrMod = user?.role === "admin" || user?.role === "moderator";

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";

  // Don't render deleted messages
  if (message.isDeleted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          padding: "8px 12px",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "12px",
          color: getTextSecondary(),
          fontSize: "11px",
          textAlign: "center",
          fontStyle: "italic",
          border: `1px solid ${getBorderColor()}`,
          margin: "8px 0",
        }}
      >
        This message was deleted
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.3,
          type: "spring",
          stiffness: 400,
          damping: 25,
        }}
        style={{
          display: "flex",
          justifyContent: isCurrentUser ? "flex-end" : "flex-start",
          marginBottom: "16px",
        }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <motion.div
          whileHover={{ scale: 1.01 }}
          style={{
            maxWidth: "75%",
            background: isCurrentUser
              ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
              : getCardBackground(),
            backdropFilter: isCurrentUser ? "none" : "blur(20px)",
            borderRadius: "20px",
            padding: "10px 14px",
            position: "relative",
            border: isCurrentUser ? "none" : `1px solid ${getBorderColor()}`,
            boxShadow: isCurrentUser
              ? "0 4px 14px 0 rgba(139,92,246,0.3)"
              : "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          {/* Sender Name (not for current user) */}
          {!isCurrentUser && (
            <div
              style={{
                fontSize: "11px",
                fontWeight: "600",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "4px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              {message.senderName}
              {message.senderRole === "admin" && (
                <span
                  style={{
                    fontSize: "9px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  <FaCrown size={8} /> Admin
                </span>
              )}
              {message.senderRole === "moderator" && (
                <span
                  style={{
                    fontSize: "9px",
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                  }}
                >
                  <FaShieldAlt size={8} /> Mod
                </span>
              )}
            </div>
          )}

          {/* Reply Indicator */}
          {message.replyTo && (
            <div
              style={{
                fontSize: "10px",
                color: isCurrentUser
                  ? "rgba(255,255,255,0.8)"
                  : getTextSecondary(),
                marginBottom: "6px",
                paddingLeft: "8px",
                borderLeft: `2px solid ${isCurrentUser ? "white" : "#8b5cf6"}`,
              }}
            >
              <FaReply size={10} style={{ marginRight: "4px" }} />
              Replying to {message.replyTo.senderName}
            </div>
          )}

          {/* Message Content */}
          <p
            style={{
              color: isCurrentUser ? "white" : getTextColor(),
              fontSize: "13px",
              margin: 0,
              wordWrap: "break-word",
              lineHeight: "1.5",
            }}
          >
            {message.profanityFiltered ? (
              <span
                title="This message contained filtered content"
                style={{ fontStyle: "italic", opacity: 0.7 }}
              >
                {message.message}
              </span>
            ) : (
              message.message
            )}
          </p>

          {/* Message Footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "6px",
              fontSize: "9px",
              color: isCurrentUser
                ? "rgba(255,255,255,0.7)"
                : getTextSecondary(),
            }}
          >
            <span>
              {formatDistanceToNow(new Date(message.createdAt), {
                addSuffix: true,
              })}
            </span>
            {message.isEdited && <span>(edited)</span>}
            {isCurrentUser && (
              <span
                style={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                <FaCheck size={10} />
                {message.readBy?.length > 1 && <FaCheckDouble size={10} />}
              </span>
            )}
          </div>

          {/* Reactions */}
          {message.reactions && message.reactions.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                marginTop: "8px",
              }}
            >
              {Object.entries(
                message.reactions.reduce((acc, r) => {
                  acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                  return acc;
                }, {}),
              ).map(([emoji, count]) => (
                <motion.button
                  key={emoji}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    cursor: "pointer",
                    border: `1px solid ${getBorderColor()}`,
                    color: getTextColor(),
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={() => handleReaction(emoji)}
                >
                  {emoji}{" "}
                  <span style={{ fontSize: "10px", fontWeight: "500" }}>
                    {count}
                  </span>
                </motion.button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 5 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 5 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: "absolute",
                  top: "-35px",
                  right: isCurrentUser ? "auto" : "0",
                  left: isCurrentUser ? "0" : "auto",
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "30px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  padding: "6px 12px",
                  display: "flex",
                  gap: "8px",
                  border: `1px solid ${getBorderColor()}`,
                  zIndex: 10,
                }}
              >
                {/* Add Reaction */}
                <div style={{ position: "relative" }}>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowReactions(!showReactions)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: getTextSecondary(),
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <FaSmile size={12} />
                  </motion.button>
                  <AnimatePresence>
                    {showReactions && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          left: "0",
                          marginBottom: "8px",
                          background: getCardBackground(),
                          backdropFilter: "blur(20px)",
                          borderRadius: "30px",
                          padding: "8px 12px",
                          display: "flex",
                          gap: "8px",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                          border: `1px solid ${getBorderColor()}`,
                          zIndex: 20,
                        }}
                      >
                        {reactions.map((emoji) => (
                          <motion.button
                            key={emoji}
                            whileHover={{ scale: 1.2, rotate: 10 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleReaction(emoji)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "18px",
                              padding: "4px",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = isDarkMode
                                ? "rgba(139,92,246,0.1)"
                                : "rgba(139,92,246,0.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            {emoji}
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Reply */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onReply(message)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: getTextSecondary(),
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#8b5cf6";
                    e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = getTextSecondary();
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <FaReply size={12} />
                </motion.button>

                {/* Report (not for own messages) */}
                {!isCurrentUser && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowReportModal(true)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: getTextSecondary(),
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = getTextSecondary();
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <FaFlag size={12} />
                  </motion.button>
                )}

                {/* Delete (for own messages or admin) */}
                {(isCurrentUser || isAdminOrMod) && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleDelete}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: getTextSecondary(),
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = getTextSecondary();
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <FaTrash size={12} />
                  </motion.button>
                )}

                {/* Pin (admin/mod only) */}
                {isAdminOrMod && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handlePin}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: message.isPinned ? "#f59e0b" : getTextSecondary(),
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#f59e0b";
                      e.currentTarget.style.background = isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "rgba(139,92,246,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (!message.isPinned) {
                        e.currentTarget.style.color = getTextSecondary();
                      }
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <FaThumbtack size={12} />
                  </motion.button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: "24px",
                maxWidth: "420px",
                width: "90%",
                border: `1px solid ${getBorderColor()}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "rgba(239,68,68,0.1)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFlag size={16} color="#ef4444" />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#ef4444",
                  }}
                >
                  Report Message
                </h3>
              </div>
              <p
                style={{
                  marginBottom: "16px",
                  fontSize: "13px",
                  color: getTextSecondary(),
                }}
              >
                Why are you reporting this message?
              </p>

              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  background: getCardBackground(),
                  color: getTextColor(),
                  fontSize: "13px",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              >
                <option value="">Select a reason</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="hate_speech">Hate Speech</option>
                <option value="inappropriate_content">
                  Inappropriate Content
                </option>
                <option value="personal_info">Personal Information</option>
                <option value="other">Other</option>
              </select>

              <textarea
                placeholder="Additional details (optional)"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows="3"
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "16px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  resize: "vertical",
                  background: getCardBackground(),
                  color: getTextColor(),
                  fontSize: "13px",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              />

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setShowReportModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: isDarkMode
                      ? "rgba(100,116,139,0.3)"
                      : "#e2e8f0",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: getTextColor(),
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReport}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  Submit Report
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatMessage;
